import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Sample } from '../../services/sample.service';
import { resolveSampleUsageActionUi } from '../../utils/sampleUsageActionUi';
import { formatEntityDisplayName } from '../../utils/softBreakIdentifier';

export type CaseSampleRowActionsProps = {
  sample: Sample;
  canManage: boolean;
  manageDisabledTitle?: string;
  onDetail: () => void;
  onEdit: (sample: Sample) => void;
  onDelete: (sample: Sample) => void;
  onAddUsage: (sample: Sample) => void;
};

/**
 * ケース一覧行の操作（使用 · 詳細 · 編集 · 削除 / モバイル ⋯）
 */
export default function CaseSampleRowActions({
  sample,
  canManage,
  manageDisabledTitle,
  onDetail,
  onEdit,
  onDelete,
  onAddUsage,
}: CaseSampleRowActionsProps) {
  const { t } = useTranslation();
  const sampleDisplayName = formatEntityDisplayName(sample.name, t);
  const usageUi = resolveSampleUsageActionUi(sample, { canManage, t });
  const detailLabel = t('sample.open_detail_page', {
    defaultValue: t('sample.detail'),
  });
  const detailShort = t('sample.open_detail_short', {
    defaultValue: detailLabel,
  });
  const detailHint = `${detailLabel} · ${sampleDisplayName}`;
  const editLabel = t('common.edit');
  const deleteLabel = t('common.delete');

  return (
    <div className="case-sample-row-actions" role="group" aria-label={t('common.actions')}>
      {usageUi.kind === 'button' ? (
        <Button
          variant="link"
          size="sm"
          className="sample-row-action-btn text-primary case-sample-icon-btn case-sample-usage-btn"
          onClick={() => onAddUsage(sample)}
          aria-label={t('sample.add_usage_record_short', {
            defaultValue: t('sample.add_usage_record'),
          })}
          title={usageUi.title}
        >
          <i className="fas fa-notes-medical" aria-hidden="true" />
          <span className="case-sample-usage-label" aria-hidden="true">
            {t('sample.add_usage_record_short', {
              defaultValue: t('sample.add_usage_record'),
            })}
          </span>
        </Button>
      ) : (
        <span
          className="sample-row-usage-blocked align-self-center"
          role="status"
          title={usageUi.title}
          aria-label={usageUi.title}
        >
          {usageUi.shortLabel}
        </span>
      )}
      <Button
        variant="link"
        size="sm"
        className="sample-row-action-btn text-secondary case-sample-icon-btn d-none d-md-inline-flex"
        onClick={onDetail}
        aria-label={detailShort}
        title={detailHint}
      >
        <i className="fas fa-eye" aria-hidden="true" />
      </Button>
      <Button
        variant="link"
        size="sm"
        className="sample-row-action-btn text-primary case-sample-icon-btn d-none d-md-inline-flex"
        onClick={() => onEdit(sample)}
        disabled={!canManage}
        aria-label={editLabel}
        title={
          manageDisabledTitle ??
          t('sample.edit_sample_description', {
            defaultValue: 'サンプル情報を編集します',
          })
        }
      >
        <i className="fas fa-pen" aria-hidden="true" />
      </Button>
      <Button
        variant="link"
        size="sm"
        className="sample-row-action-btn text-danger case-sample-icon-btn d-none d-md-inline-flex"
        onClick={() => onDelete(sample)}
        disabled={!canManage}
        aria-label={deleteLabel}
        title={manageDisabledTitle ?? t('sample.delete_sample_description')}
      >
        <i className="fas fa-trash-alt" aria-hidden="true" />
      </Button>
      <Dropdown className="d-md-none" align="end">
        <Dropdown.Toggle
          variant="link"
          size="sm"
          className="sample-row-action-btn text-secondary case-sample-icon-btn case-sample-more-toggle"
          id={`case-sample-more-${sample.id}`}
          title={t('sample.row_more_actions_title', {
            name: sampleDisplayName,
            defaultValue: t('common.more_actions'),
          })}
          aria-label={t('sample.row_actions_aria', {
            name: sampleDisplayName,
            defaultValue: `${t('common.actions')} · ${sampleDisplayName}`,
          })}
        >
          <i className="fas fa-ellipsis-v" aria-hidden="true" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={onDetail} title={detailHint}>
            <i className="fas fa-eye me-2" aria-hidden="true" />
            {detailShort}
          </Dropdown.Item>
          <Dropdown.Item
            disabled={!canManage}
            onClick={() => onEdit(sample)}
            title={
              manageDisabledTitle ??
              t('sample.edit_sample_description', {
                defaultValue: 'サンプル情報を編集します',
              })
            }
          >
            <i className="fas fa-pen me-2" aria-hidden="true" />
            {editLabel}
          </Dropdown.Item>
          <Dropdown.Item
            disabled={!canManage}
            onClick={() => onDelete(sample)}
            title={manageDisabledTitle ?? t('sample.delete_sample_description')}
            className="text-danger"
          >
            <i className="fas fa-trash-alt me-2" aria-hidden="true" />
            {deleteLabel}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
